import { useEffect, useState } from "react";
import { AppLayout, RiskBanner } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Copy, ArrowDownToLine, CheckCircle2 } from "lucide-react";
import QRCode from "react-qr-code";

export default function Deposit() {
  const { refresh } = useAuth();
  const [wallet, setWallet] = useState({ BEP20: "", min_deposit: 10, verification_mode: "simple" });
  const [txHash, setTxHash] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const [{ data: w }, { data: h }] = await Promise.all([api.get("/wallet/addresses"), api.get("/deposits/my")]);
    setWallet(w); setHistory(h.deposits);
  };
  useEffect(() => { load(); }, []);

  const copy = async () => { await navigator.clipboard.writeText(wallet.BEP20); toast.success("BEP20 address copied"); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // SECURITY: We NEVER send an amount — the server derives it from the on-chain tx.
      const { data } = await api.post("/deposits/submit", { network: "BEP20", tx_hash: txHash });
      if (data.status === "pending") {
        toast.info(data.message || "Deposit submitted, awaiting on-chain confirmation");
      } else {
        toast.success(data.message || `Deposit of $${(data.amount||0).toFixed(2)} verified & credited`);
      }
      setTxHash(""); await load(); await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <AppLayout variant="user">
      <RiskBanner />
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Deposit USDT (BEP20)</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1 flex items-center gap-3"><ArrowDownToLine className="text-[#8AE234]"/> Fund your account</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">Send USDT via <strong className="text-white">BEP20 network only</strong>. Paste your transaction hash below — the <span className="text-[#8AE234] font-semibold">exact amount is auto-detected on-chain</span> and credited instantly. You don&apos;t need to type any amount.</p>
      </div>

      <div className="mb-4 rounded-xl border border-nb-cyan/30 bg-nb-cyan/5 px-4 py-3 text-xs text-nb-cyan flex items-start gap-2" data-testid="onchain-badge">
        <CheckCircle2 size={14} className="mt-0.5 shrink-0"/>
        <span><strong>Amount is auto-verified from your on-chain transaction — you cannot change it.</strong> Whatever USDT you sent (min ${wallet.min_deposit}, max unlimited) is what gets credited.</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div className="flex justify-center bg-white rounded-2xl p-4">
              {wallet.BEP20 && <QRCode value={wallet.BEP20} size={180} bgColor="#FFFFFF" fgColor="#000000"/>}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">Company BEP20 Address</div>
              <div className="mt-2 rounded-xl bg-[#070B14] border border-white/10 p-3 flex items-center gap-2">
                <div className="font-mono text-xs text-slate-200 break-all flex-1" data-testid="wallet-BEP20">{wallet.BEP20}</div>
                <button onClick={copy} data-testid="btn-copy-wallet" className="text-nb-cyan hover:text-white shrink-0"><Copy size={16}/></button>
              </div>
              <div className="mt-4 space-y-2 text-xs text-slate-400">
                <div>• Network: <span className="text-white font-semibold">BEP20 (BSC)</span> only</div>
                <div>• Minimum deposit: <span className="text-white font-mono">${wallet.min_deposit}</span> · Maximum: <span className="text-white">Unlimited</span></div>
                <div>• Each transaction hash can be submitted <strong className="text-amber-300">only once</strong></div>
                <div>• Wrong network transfers are unrecoverable</div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-3 border-t border-white/10 pt-6">
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-widest">Transaction Hash (TxID)</label>
              <input data-testid="deposit-tx" required value={txHash} onChange={(e)=>setTxHash(e.target.value)} className="nb-input mt-1 font-mono" placeholder="0x…"/>
              <div className="text-[11px] text-slate-500 mt-1">Copy the TxID from your wallet after sending — it looks like <code className="text-slate-300">0xabc123…</code>. We&apos;ll verify it on BSC and credit the exact amount you sent.</div>
            </div>
            <button data-testid="deposit-submit" disabled={busy} className="nb-btn-primary w-full">{busy?"Verifying on-chain…":"Verify & Credit"}</button>
          </form>
        </div>

        <div className="nb-card p-6">
          <div className="font-heading font-semibold text-white mb-4">Deposit history</div>
          {history.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No deposits yet.</div> : (
            <div className="divide-y divide-white/5">
              {history.map((d) => (
                <div key={d.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-mono text-white flex items-center gap-1">${d.amount.toFixed(2)} {d.auto_verified && <CheckCircle2 size={12} className="text-[#8AE234]"/>}</div>
                      <div className="text-xs text-slate-500 font-mono truncate max-w-[240px]">{d.tx_hash}</div>
                    </div>
                    <div className={`text-xs uppercase tracking-widest px-2 py-1 rounded ${d.status==="approved"?"text-[#8AE234] bg-[#8AE234]/10":d.status==="pending"?"text-amber-300 bg-amber-500/10":"text-red-400 bg-red-500/10"}`}>{d.status}</div>
                  </div>
                  {d.onchain_check && (
                    <div className="mt-1 text-[10px] text-slate-500 font-mono">
                      {d.onchain_check.verified ? "✅ " : "⏳ "}{d.onchain_check.message}
                      {d.onchain_check.confirmations > 0 && ` · ${d.onchain_check.confirmations} conf`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
